import { useState } from "react";
import type { FeedPost } from "../../../mocks/community";

const AI_TOOLS = ["ChatGPT", "Midjourney", "Runway", "Claude", "Stable Diffusion", "Gemini", "기타"];

interface Props {
  onClose: () => void;
  onSubmit: (post: FeedPost) => void;
  nextId: number;
}

export default function FailPostModal({ onClose, onSubmit, nextId }: Props) {
  const [step, setStep] = useState<"form" | "done">("form");
  const [tool, setTool] = useState("");
  const [intent, setIntent] = useState("");
  const [failPrompt, setFailPrompt] = useState("");
  const [failReason, setFailReason] = useState("");
  const [failQuestion, setFailQuestion] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!tool) errs.tool = "AI 툴을 선택해주세요";
    if (!intent.trim()) errs.intent = "무엇을 만들려 했는지 입력해주세요";
    if (!failPrompt.trim()) errs.failPrompt = "사용한 프롬프트를 입력해주세요";
    if (!failReason.trim()) errs.failReason = "어떤 문제가 발생했는지 설명해주세요";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const avatarSeeds = ["fpf1", "fpf2", "fpf3", "fpf4", "fpf5"];
    const seed = avatarSeeds[Math.floor(Math.random() * avatarSeeds.length)];

    const newPost: FeedPost = {
      id: nextId,
      user: {
        name: "나",
        avatar: `https://public.readdy.ai/ai/img_res/8e1164301c73c6234c8beb9748ecf005.jpg${seed}&orientation=squarish`,
      },
      type: "fail",
      content: `[${tool}] ${intent}`,
      image: null,
      stamp: null,
      claps: 0,
      fire: 0,
      spark: 0,
      time: "방금 전",
      height: "medium",
      failPrompt: failPrompt.trim(),
      failReason: failReason.trim(),
      failQuestion: failQuestion.trim() || undefined,
    };

    onSubmit(newPost);
    setStep("done");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[#F2F4F6] flex-shrink-0">
          <div className="w-8 h-8 rounded-xl bg-red-100 flex items-center justify-center">
            <span className="text-base">💥</span>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-extrabold text-[#191F28]">실패 경험 공유하기</h3>
            <p className="text-[11px] text-[#8B95A1]">Kolb 경험 학습 — 실패에서 함께 배워요</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#F2F4F6] flex items-center justify-center cursor-pointer hover:bg-[#E8E9EB] transition-colors"
          >
            <i className="ri-close-line text-[#8B95A1] text-base" />
          </button>
        </div>

        {step === "done" ? (
          /* ── Success state ── */
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
            <div className="text-5xl mb-4">🎉</div>
            <h4 className="text-xl font-extrabold text-[#191F28] mb-2">용감하게 공유해줬어요!</h4>
            <p className="text-sm text-[#8B95A1] mb-2 leading-relaxed">
              실패를 공유하는 것이 진짜 용기있는 학습이에요.<br />
              커뮤니티가 힌트를 줄 거예요 💪
            </p>
            <div className="bg-[#F0FDF7] border border-[#00D873]/30 rounded-xl px-4 py-3 mb-6">
              <p className="text-xs text-[#2D6A4F] font-semibold leading-relaxed">
                💡 Kolb 경험 학습 이론 — 실패를 언어화하고 공유하는 순간, 학습이 시작돼요
              </p>
            </div>
            <button
              onClick={onClose}
              className="px-8 py-3 bg-[#191F28] text-white font-bold rounded-2xl cursor-pointer hover:opacity-90 transition whitespace-nowrap"
            >
              피드에서 확인하기
            </button>
          </div>
        ) : (
          /* ── Form ── */
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* AI 툴 */}
            <div>
              <label className="block text-xs font-bold text-[#191F28] mb-2">
                어떤 AI 툴을 사용했나요?
                <span className="text-red-400 ml-0.5">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {AI_TOOLS.map((t) => (
                  <button
                    key={t}
                    onClick={() => { setTool(t); setErrors((e) => ({ ...e, tool: "" })); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      tool === t
                        ? "bg-[#191F28] text-white"
                        : "bg-[#F2F4F6] text-[#3D4756] hover:bg-[#E8E9EB]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {errors.tool && <p className="text-xs text-red-500 mt-1">{errors.tool}</p>}
            </div>

            {/* 무엇을 만들려 했나요 */}
            <div>
              <label className="block text-xs font-bold text-[#191F28] mb-2">
                무엇을 만들려 했나요?
                <span className="text-red-400 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={intent}
                onChange={(e) => { setIntent(e.target.value); setErrors((err) => ({ ...err, intent: "" })); }}
                placeholder="예: 브랜드 로고 이미지, 주간 보고서, 유튜브 썸네일..."
                className="w-full bg-[#F2F4F6] rounded-xl px-4 py-3 text-sm text-[#191F28] placeholder-[#C4C9D4] outline-none focus:ring-2 focus:ring-[#191F28]/20 transition-all"
              />
              {errors.intent && <p className="text-xs text-red-500 mt-1">{errors.intent}</p>}
            </div>

            {/* 실패한 프롬프트 */}
            <div>
              <label className="block text-xs font-bold text-[#191F28] mb-2">
                사용한 프롬프트
                <span className="text-red-400 ml-0.5">*</span>
              </label>
              <textarea
                value={failPrompt}
                onChange={(e) => { setFailPrompt(e.target.value); setErrors((err) => ({ ...err, failPrompt: "" })); }}
                placeholder="AI에게 입력한 프롬프트를 그대로 붙여넣어 주세요..."
                rows={4}
                maxLength={500}
                className="w-full bg-[#F8F9FA] border border-[#E8E9EB] rounded-xl px-4 py-3 text-sm text-[#191F28] placeholder-[#C4C9D4] font-mono outline-none focus:ring-2 focus:ring-red-200 transition-all resize-none"
              />
              <p className="text-[10px] text-[#C4C9D4] text-right mt-0.5">{failPrompt.length}/500</p>
              {errors.failPrompt && <p className="text-xs text-red-500">{errors.failPrompt}</p>}
            </div>

            {/* 어떤 문제가 발생했나요 */}
            <div>
              <label className="block text-xs font-bold text-[#191F28] mb-2">
                어떤 문제가 발생했나요?
                <span className="text-red-400 ml-0.5">*</span>
              </label>
              <textarea
                value={failReason}
                onChange={(e) => { setFailReason(e.target.value); setErrors((err) => ({ ...err, failReason: "" })); }}
                placeholder="예: 텍스트가 이상하게 깨짐, 이미지 비율이 틀림, 결과물이 너무 딱딱함..."
                rows={3}
                maxLength={300}
                className="w-full bg-[#F2F4F6] rounded-xl px-4 py-3 text-sm text-[#191F28] placeholder-[#C4C9D4] outline-none focus:ring-2 focus:ring-red-200 transition-all resize-none"
              />
              <p className="text-[10px] text-[#C4C9D4] text-right mt-0.5">{failReason.length}/300</p>
              {errors.failReason && <p className="text-xs text-red-500">{errors.failReason}</p>}
            </div>

            {/* 커뮤니티에 물어볼 것 */}
            <div>
              <label className="block text-xs font-bold text-[#191F28] mb-1">
                커뮤니티에 물어볼 것
                <span className="text-[#8B95A1] font-normal ml-1">(선택)</span>
              </label>
              <p className="text-[11px] text-[#8B95A1] mb-2">힌트나 개선 방법을 물어보세요!</p>
              <input
                type="text"
                value={failQuestion}
                onChange={(e) => setFailQuestion(e.target.value)}
                placeholder="예: 텍스트 없이 브랜드 느낌만 살리는 방법 아시는 분?"
                className="w-full bg-[#F2F4F6] rounded-xl px-4 py-3 text-sm text-[#191F28] placeholder-[#C4C9D4] outline-none focus:ring-2 focus:ring-[#191F28]/20 transition-all"
              />
            </div>

            {/* Kolb tip */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-100 rounded-xl p-3 flex gap-2">
              <span className="text-base flex-shrink-0">🧪</span>
              <p className="text-xs text-[#3D4756] leading-relaxed">
                <strong>실패를 언어화하는 것 자체가 학습이에요.</strong> 무엇이 잘못됐는지 직접 설명하면서 다음 번에는 더 잘할 수 있게 돼요.
              </p>
            </div>
          </div>
        )}

        {/* Footer CTA */}
        {step === "form" && (
          <div className="flex-shrink-0 px-6 py-4 border-t border-[#F2F4F6]">
            <button
              onClick={handleSubmit}
              className="w-full py-3.5 bg-[#191F28] text-white font-extrabold text-sm rounded-2xl cursor-pointer hover:opacity-90 transition whitespace-nowrap"
            >
              💥 실패 경험 공유하기
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
